import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

import type { ProgramControlCommonProps, SubmitReportFn } from '../controlTypes';

type ProgressHubOption = { label: string; value: string };

type ProgressReportProgramControlsProps = ProgramControlCommonProps & {
  progressReportHubOptions: ProgressHubOption[];
  progressReportAppearanceOptions: ProgressHubOption[];
  progressReportProviderSuggestions: string[];
  onSubmitReport: SubmitReportFn;
};

export const ProgressReportProgramControls = ({
  values,
  onFilterChange,
  actionRowClass,
  renderStandardActionButtons,
  renderReportActionLabel,
  progressReportHubOptions,
  progressReportAppearanceOptions,
  progressReportProviderSuggestions,
  onSubmitReport,
}: ProgressReportProgramControlsProps) => (
  <div className="mb-3 flex flex-wrap items-end justify-between gap-4 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-white px-4 py-3">
    <div className="flex flex-wrap items-center gap-6">
      <div className="flex items-center gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Provider Submitted</div>
        <div className="flex items-center gap-4 text-xs text-[var(--neutral-gray-120)]">
          <label className="flex items-center gap-1.5"><input type="radio" name="progress-report-submitted" checked={values.progressReportSubmittedFilter === 'all'} onChange={() => onFilterChange('progressReportSubmittedFilter', 'all')} />All</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="progress-report-submitted" checked={values.progressReportSubmittedFilter === 'n'} onChange={() => onFilterChange('progressReportSubmittedFilter', 'n')} />Yes</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="progress-report-submitted" checked={values.progressReportSubmittedFilter === 'y'} onChange={() => onFilterChange('progressReportSubmittedFilter', 'y')} />No</label>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">ODR Review and Submitted</div>
        <div className="flex items-center gap-4 text-xs text-[var(--neutral-gray-120)]">
          <label className="flex items-center gap-1.5"><input type="radio" name="progress-report-completed" checked={values.progressReportCompletedFilter === 'all'} onChange={() => onFilterChange('progressReportCompletedFilter', 'all')} />All</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="progress-report-completed" checked={values.progressReportCompletedFilter === 'y'} onChange={() => onFilterChange('progressReportCompletedFilter', 'y')} />Yes</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="progress-report-completed" checked={values.progressReportCompletedFilter === 'n'} onChange={() => onFilterChange('progressReportCompletedFilter', 'n')} />No</label>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Hub</div>
        <select
          aria-label="Hub"
          value={values.progressReportHubFilter}
          onChange={(event) => onFilterChange('progressReportHubFilter', String(event.target.value || 'all'))}
          className="h-8 min-w-[150px] rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-xs text-[var(--neutral-gray-120)]"
        >
          <option value="all">All</option>
          {progressReportHubOptions.map((hub) => (
            <option key={hub.value} value={hub.value}>{hub.label}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Appearance</div>
        <select
          aria-label="Appearance"
          value={values.progressReportAppearanceFilter}
          onChange={(event) => onFilterChange('progressReportAppearanceFilter', String(event.target.value || 'all'))}
          className="h-8 min-w-[170px] rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-xs text-[var(--neutral-gray-120)]"
        >
          <option value="all">All</option>
          {progressReportAppearanceOptions.map((appearance) => (
            <option key={appearance.value} value={appearance.value}>{appearance.label}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">ICMS Provider</div>
        <Input
          aria-label="ICMS Provider"
          list="progress-report-provider-list"
          value={values.progressReportIcmsProviderFilter}
          onChange={(event) => onFilterChange('progressReportIcmsProviderFilter', event.target.value)}
          className="h-8 w-[220px]"
          placeholder="Exact provider"
        />
        <datalist id="progress-report-provider-list">
          {progressReportProviderSuggestions.map((provider) => (
            <option key={provider} value={provider} />
          ))}
        </datalist>
      </div>

      <div className="flex items-center gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Court Date</div>
        <Input
          aria-label="Court Date"
          type="date"
          value={values.progressReportCourtDateFilter}
          onChange={(event) => onFilterChange('progressReportCourtDateFilter', event.target.value)}
          className="h-8 w-[160px]"
        />
      </div>
    </div>

    <div className={actionRowClass}>
      {renderStandardActionButtons()}
      <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => onSubmitReport({ action: 'all-report', download: '1' })}>
        {renderReportActionLabel('All Progress Reports')}
      </Button>
    </div>
  </div>
);
